<template>
  <base-collapse
    v-model="modelValue"
    class="f-col"
    :class="[brandClasses.wrapper, { 'children:px-4': !variant }, { 'pb-3': isExpanded }]"
    :class-content="[
      classContent,
      isExpanded ? 'animation-ease-out @duration-500' : 'animation-ease-in @duration-400',
    ]"
    :class-toggle
    data-test-id="vf-accordion"
    disable-visibility-toggle
    :value
    @close="emit('close')"
    @expand="emit('expand')"
  >
    <template #toggle="{ expanded }">
      <slot :expanded name="toggle">
        <span class="flex items-center py-4 between" data-test-id="vf-accordion-toggle">
          <div class="flex gap-2">
            <vf-icon
              v-if="titleIcon"
              v-style:c="titleColor"
              :dir="titleIcon.dir"
              :name="titleIcon.icon"
              :size
              :src="titleIcon.src"
            />
            <span v-style:c="titleColor" :class="classTitle ?? brandClasses.title[size]">
              <slot name="title">{{ title }}</slot>
            </span>
          </div>
          <template v-if="iconCollapseName">
            <span
              v-style:c="titleColor"
              class="relative shrink-0 items-center i-flex"
              :class="classToggleIcon ?? 'h-5 w-5'"
            >
              <!-- Horizontal bar — always visible (the minus) -->
              <span class="absolute inset-x-0 top-1/2 h-0.5 bg-current -translate-y-px" />
              <!-- Vertical bar — rotates into vertical to form plus -->
              <span
                class="absolute left-1/2 top-1/2 h-0.5 w-full bg-current transition-transform @duration-400 -translate-x-1/2 -translate-y-px"
                :class="{ 'rotate-90': !expanded }"
              />
            </span>
          </template>
          <vf-icon
            v-else
            v-style:c="titleColor"
            class="transition-transform @duration-400"
            :class="{ '-rotate-180': expanded }"
            :name="iconName"
            :size="iconSize[size]"
          />
        </span>
      </slot>
    </template>
    <template #default>
      <div :class="brandClasses.content" data-test-id="vf-accordion-content">
        <slot />
      </div>
    </template>
  </base-collapse>
</template>

<script lang="ts" setup>
import type { Emits, Props, Slots } from '#brands/components/vf/accordion/Accordion.vue'

const { classContent, classToggleIcon, icon, iconCollapse, size = 'md', value } = defineProps<Props>()

const emit = defineEmits<Emits>()

defineSlots<Slots>()

const modelValue = defineModel<boolean | string>('open')

const isExpanded = computed(() => !!modelValue.value)

const { brandClasses, fallbackIcon, fallbackIconCollapse, iconSize } = useAppConfig().components.vf.accordion

const iconName = computed(() => icon || fallbackIcon)
const iconCollapseName = computed(() => iconCollapse ?? fallbackIconCollapse)
</script>
